
import ApiService from "services/ApiService";
import { AdayaError } from "../AppErrors";
import { TaskStatus } from "../data/models/Tasks";
import { getInnerError } from "./adaya/AdayaUtils";

/**
 * @description This gets passed an async method, which returns the result of it.
 *  If the method happens to throw an exception, it will retry the request.
 * @param {*} req
 * @param {number} [attempts=0]
 * @param {number} [maxAttempts=2]
 * @param {number} [sleepBetweenMs=0] If > 0, will pause ms between each retry.
 * @return {*}
 */
export const ensureRequest = async (reqFn, retries = 2, errorCallback?) => {
    try {
        if (retries >= 0) {
            return await reqFn();
        } else {
            console.log(`ensureRequest stopping, at max retries:`, reqFn, retries);
            return undefined;
        }
    } catch (e) {
        console.log('Request error:\n', e, '\nFor request:\b', reqFn, `\n ...retrying (${retries - 1} retries left)...`);
        if (errorCallback) errorCallback(e);
        //if (sleepBetween) await sleep(sleepBetweenMs);
        return await ensureRequest(reqFn, retries - 1, errorCallback);
    }
};

/**
 * @description This wraps a given async method (the request), which is assumed to either return a taskId, or the data.
 * This method will poll for the data if a task is returned, and return the data when ready.
 * @param {*} request Must be an async method taking one parameter (isComplete) which signifies if the job has completed successfully.
 * @return {*} The request (ie. { success, data, error }
 */
export const handlePolledRequest = async (request, timeout = 1500) => {
    try {
        let r = await request(false);
        if (r?.success) {
            if (r.taskId) { // a job was returned from the initial request...
                let pr = await ApiService.pollForTaskCompletion(r.taskId, null, timeout);
                if (pr) {
                    if (pr.status == TaskStatus.Finished) {
                        // try for the data again, should be there.
                        let r2 = await request(true);
                        if (r2?.success) {
                            if (r2.taskId) {
                                console.warn("❓ Warning: duplicate task started with same params:", r2.taskId, ', original task:', r.taskId);
                            } else {
                                r = r2; // replace with second request
                            }
                        }
                    } else if (pr.status == TaskStatus.FinishedWithErrors) {
                        console.log(`FINISHED WITH ERRORS`, pr)
                        r = pr;
                    } else if (pr.status == TaskStatus.Error) {
                        // shouldn't happen? the errors throw an exception, I think.
                        r = pr;
                    }
                }
            }

            return r;
        } else {
            console.log(`handlePolled error?`, r)
            throw new AdayaError(getInnerError(r));
        }
    } catch (e) {
        console.log('Job Exception:', e);
        throw e;
    }
};

/**
 * @description Try the async function up until opts.retries, with opts.waitBetweenMs delay between each,
 * until it returns a truthy value, otherwise it returns undefined.
 * @param {*} reqFn
 * @param {*} [opts] { retries: int, waitBeweenMs: int }
 * @return {*}
 */
export const tryUntilSuccess = async (reqFn, opts?) => {
    return new Promise(async (resolve, reject) => {
        try {
            const tryFor = async (attempt = 0) => {
                try {
                    if (attempt > opts.retries) return resolve(undefined);
                    const r = await reqFn();
                    if (r) return resolve(r);
                    setTimeout(async () => await tryFor(++attempt), opts.waitBetween);
                } catch (e) {
                    // let it retry
                    console.log(`Exception in tryUntilSuccess:`, e);
                    if (++attempt > opts.retries) return reject(e);
                    setTimeout(async () => await tryFor(attempt), opts.waitBetween);
                }
            }

            return await tryFor();
        } catch (e) {
            return reject(e);
        }
    });
};

